import { SharedUiColorNameV2 } from '@/shared/ui/theming';
import { UiFormErrorComponent } from '@/ui/form-field/form-error/ui-form-error';
import { UiFormFieldComponent } from '@/ui/form-field/form-field/ui-form-field';
import { UiFormHintComponent } from '@/ui/form-field/form-hint/ui-form-hint';
import { observeControlValidity$ } from '@/utils/angular/forms/reactive-forms/reactive-forms-rxjs-interop.utils';
import { KeyValuePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, type OnInit, output, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import { AdvizeoInputDirective } from '../../../../directives/advizeo-input/advizeo-input.directive';
import {
    type AdvizeoModalButton,
    type AdvizeoModalComponent,
    injectAdvizeoModalInput
} from '../../../advizeo-modal-spawner/advizeo-modal-spawner.component';

export interface OneFieldPopoverInput {
    title: string;
    fieldLabel: string;
    submitValidators: Validator[];
    fieldType?: string;
    fieldValue?: string;
    infoLabel?: string;
}

export interface OneFieldPopoverOutput {
    fieldValue: string;
}
interface Validator {
    validate: (name: string) => boolean;
    errorMessage?: string;
}
@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./one-field-popover.scss'],
    selector: 'one-field-popover',
    templateUrl: 'one-field-popover.html',
    imports: [
        FormsModule,
        UiFormFieldComponent,
        AdvizeoInputDirective,
        ReactiveFormsModule,
        UiFormHintComponent,
        UiFormErrorComponent,
        KeyValuePipe,
        TranslatePipe
    ]
})
export class OneFieldPopoverComponent implements AdvizeoModalComponent<OneFieldPopoverInput, OneFieldPopoverOutput>, OnInit {
    title = signal('');
    isDismissable = signal(true);
    buttons = signal<AdvizeoModalButton[]>([]);

    input = injectAdvizeoModalInput<OneFieldPopoverInput>();
    close = output<OneFieldPopoverOutput | undefined>();

    fieldLabel!: string;
    fieldType!: string;
    valueControl!: UntypedFormControl;
    submitErrorMessage!: string;
    submitValidators!: Validator[];
    infoLabel!: string;

    ngOnInit() {
        this.title.set(this.input().title);
        this.fieldLabel = this.input().fieldLabel;
        this.fieldType = this.input().fieldType || 'text';
        this.submitValidators = this.input().submitValidators;
        this.valueControl = new UntypedFormControl(
            this.input().fieldValue,
            this.submitValidators
                ? this.submitValidators.map((validator, index) => control => {
                      if (validator.validate(control.value)) {
                          return null;
                      } else {
                          return { [index]: validator.errorMessage };
                      }
                  })
                : []
        );
        this.infoLabel = this.input().infoLabel ?? '';

        observeControlValidity$(this.valueControl)
            .pipe(untilDestroyed(this))
            .subscribe(isValid => {
                this.setButtons(isValid);
            });
    }

    doConfirm() {
        this.close.emit({
            fieldValue: this.valueControl.value
        });
    }

    setButtons(isValid: boolean) {
        this.buttons.set([
            {
                text: 'BUTTONS.VALIDATE',
                color: SharedUiColorNameV2.PRIMARY,
                isDisabled: !isValid,
                onClick: () => this.doConfirm()
            }
        ]);
    }
}
